import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Skeleton, Tabs, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";

import { useLoc, useNotifier, useSdk } from "@app/shared/lib/hooks";
// import { useUser } from "@app/shared/lib/hooks/useUser";
import { useUser } from "@app/shared/lib/hooks/useUser";
import { Loc } from "@app/shared/ui/Loc";
import { Price } from "@app/shared/ui/Price";

import { BonusTable } from "./components/BonusTable/BonusTable";
import { CommonSettingsForm } from "./components/CommonSettingsForm/CommonSettingsForm";
import { NotificationsTab } from "./components/NotificationsTab/NotificationsTab";

const BonusTab: React.FC = () => {
  const { user } = useUser();

  return (
    <>
      <Typography.Title level={3}>
        <Loc word="Contact.bonusBalance" default="Bonus balance" />
        :&nbsp;
        {typeof user?.bonusBalance === "number" && (
          <Price
            price={user.bonusBalance}
            truncateIfFractionalZero
            className="fw-500"
          />
        )}
      </Typography.Title>
      <div>
        <Typography.Title level={3} style={{ marginBottom: 10 }}>
          <Loc
            word="Partnership.bonus.transactions"
            default="Bonus transactions"
          />
        </Typography.Title>

        <BonusTable />
      </div>
    </>
  );
};

const QRCodeTab: React.FC = () => {
  const sdk = useSdk();
  const loc = useLoc();
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [notify] = useNotifier();

  const getQrCode = useCallback(async () => {
    const result = await sdk.api.auth.getQr();
    setQrCode(result.body);
    notify(result);
  }, [notify, sdk.api.auth]);

  const reloadQrCode = useCallback(async () => {
    const res = await sdk.api.auth.getQr();
    notify(
      res,
      loc.word("QRCode.reloadSuccess", {
        default: "QR code reloaded successfully",
      }),
    );
    setQrCode(res.body);
  }, [loc, notify, sdk.api.auth]);

  useEffect(() => {
    getQrCode();
  }, [getQrCode]);

  return (
    <div className="flex flex-col justify-center align-center">
      <Alert
        showIcon
        type="info"
        title={loc.word("QRCode.help", {
          default: "Scan the QR code from AXL app to login",
        })}
      />
      <div className="p-10">
        {qrCode ? (
          <img
            src={`data:image/png;base64,${qrCode}`}
            alt="QR Code"
            width={280}
            height={280}
          />
        ) : (
          <Skeleton.Image active style={{ width: 280, height: 280 }} />
        )}
      </div>
      <Button type="primary" onClick={reloadQrCode} icon={<ReloadOutlined />}>
        <Loc word="QRCode.refresh" default="Refresh" />
      </Button>
    </div>
  );
};

type Props = {
  defaultTab?: "common" | "notifications" | "bonus" | "qrcode";
  viewMode: "default" | "required";
  onSave?: () => void;
};

export const StudentProfileForm: React.FC<Props> = ({
  defaultTab,
  viewMode,
  onSave,
}) => {
  const loc = useLoc();

  if (viewMode === "required")
    return (
      <div className="p-15">
        <CommonSettingsForm viewMode={viewMode} onSave={onSave} />
      </div>
    );

  return (
    <Tabs defaultActiveKey={defaultTab ?? "common"} animated={false}>
      <Tabs.TabPane tab={loc.word("Common")} key="common" className="p-15">
        <CommonSettingsForm viewMode={viewMode} onSave={onSave} />
      </Tabs.TabPane>
      <Tabs.TabPane
        tab={loc.word("NotifLabel")}
        key="notifications"
        className="p-15"
      >
        <NotificationsTab />
      </Tabs.TabPane>
      <Tabs.TabPane
        tab={<Loc word="Contact.bonusBalance" />}
        key="bonus"
        className="p-15"
      >
        <BonusTab />
      </Tabs.TabPane>
      <Tabs.TabPane
        tab={loc.word("QRCode")}
        key="qrcode"
        className="pl-15 pr-15 pb-15"
      >
        <QRCodeTab />
      </Tabs.TabPane>
    </Tabs>
  );
};
